import { useLayoutEffect, useRef } from "react";

import { parseAsString, useQueryState } from "nuqs";

import { useSessionStore } from "@/entities/session";
import { Roles } from "@/entities/user";
import { useDashboardProPageConfigStore } from "./DashboardProPageConfigStore";
import {
  useLinksPageConfigStore,
  WORKER_HIDDEN_LINKS_PAGE_COLUMN_IDS,
} from "./LinksPageConfigStore";
import { LinksColumns } from "@/pages/restricted/shared/links-page/ui/links-columns";
import type { LeadLinkColumnConfig } from "../model";

export const useVisibleLinksColumns = (page: "links" | "dashboardPro") => {
  const [target] = useQueryState("target", parseAsString);
  const { user } = useSessionStore();
  const { addColumns, removeColumns, removeDuplicates } =
    useLinksPageConfigStore();
  const prevTargetRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    removeDuplicates();
  }, [removeDuplicates]);

  useLayoutEffect(() => {
    const prevTarget = prevTargetRef.current;
    const currentTarget = target;

    const additionalColumns: LeadLinkColumnConfig[] = [
      {
        id: "bot-info",
        title: "Информация о боте",
        locked: true,
        visible: true,
      },
      {
        id: "bot",
        title: "Бот",
        locked: true,
        visible: true,
      },
    ];

    if (
      currentTarget === "constructor-bot" &&
      prevTarget !== "constructor-bot"
    ) {
      addColumns(additionalColumns);
    } else if (
      prevTarget === "constructor-bot" &&
      currentTarget !== "constructor-bot"
    ) {
      removeColumns(additionalColumns);
    }

    prevTargetRef.current = currentTarget;
  }, [target, addColumns, removeColumns]);

  const { columns: linksPageConfig } = useLinksPageConfigStore();
  const { columns: dashboardProPageConfig } = useDashboardProPageConfigStore();

  const columnsConfig = () => {
    switch (page) {
      case "links":
        return linksPageConfig;
      case "dashboardPro":
        return dashboardProPageConfig;
      default:
        return [];
    }
  };

  const columns = columnsConfig();
  const isWorkerLinksPage = page === "links" && user?.role === Roles.Worker;

  const uniqueColumns = columns.filter(
    (column, index, self) =>
      index === self.findIndex((c) => c.id === column.id),
  );

  const visibleColumnIds = new Set(
    uniqueColumns
      .filter((col) => {
        if (!col.visible) return false;

        if (
          isWorkerLinksPage &&
          WORKER_HIDDEN_LINKS_PAGE_COLUMN_IDS.includes(
            col.id as (typeof WORKER_HIDDEN_LINKS_PAGE_COLUMN_IDS)[number],
          )
        ) {
          return false;
        }

        return true;
      })
      .map((col) => col.id),
  );

  return LinksColumns.filter((col) => col.id && visibleColumnIds.has(col.id));
};
